import "react-contexify/ReactContexify.css";

import { faCaretUp, faSpinner } from "@fortawesome/pro-duotone-svg-icons";
import { faAngleDown, faAngleRight, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { faUpRightAndDownLeftFromCenter } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Editor, mergeAttributes, NodeViewProps } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import {
  EditorContent,
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
} from "@tiptap/react";
import classNames from "classnames";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Item, Menu, Separator, Submenu, useContextMenu } from "react-contexify";
import { useNavigate, useParams } from "react-router-dom";
import { yXmlFragmentToProsemirrorJSON } from "y-prosemirror";
import * as Y from "yjs";

import { django } from "@/api";
import { useActiveEditor } from "@/hooks";
import useQueries from "@/hooks/useQueries";

import ListItem from "../ListItem";
import { loadExtensions } from "../loadExtensions";
import styles from "./RoadmapList.module.scss";
import { convertXmlToYjs } from "./xmlToYjsConverter";

// const ydoc = new Y.Doc()
const findChildOfType = (node: ProseMirrorNode, type: string) => {
  for (let i = 0; i < node.childCount; i++) {
    const child = node.child(i);
    if (child.type.name === type) {
      return child;
    }
  }

  return null;
};

const collapsedStore = () => {
  let items: string | null = localStorage.getItem("disco-collapsed");
  let parsedItems: { [key: string]: boolean } = {};
  if (!items) {
    parsedItems = {};
  } else {
    parsedItems = JSON.parse(`${items}`);
  }
  return parsedItems;
};

export const storeCollapsed = (id: string, collapsed: boolean) => {
  const items = collapsedStore();
  items[id] = collapsed;
  localStorage.setItem("disco-collapsed", JSON.stringify(items));
};

export const getCollapsed = (id: string) => {
  const items = collapsedStore();
  return id in items ? items[id] : false;
};

const syncedCollapsedStore = () => {
  let items: string | null = localStorage.getItem("disco-syncedCollapsed");
  let parsedItems: { [key: string]: boolean } = {};
  if (!items) {
    parsedItems = {};
  } else {
    parsedItems = JSON.parse(`${items}`);
  }
  return parsedItems;
};

export const storeSyncedCollapsed = (id: string, syncedCollapsed: boolean) => {
  const items = syncedCollapsedStore();
  items[id] = syncedCollapsed;
  localStorage.setItem("disco-syncedCollapsed", JSON.stringify(items));
};

export const getSyncedCollapsed = (id: string) => {
  const items = syncedCollapsedStore();
  return id in items ? items[id] : true;
};

const MENU_ID = "ListItem";

export const Component = memo(({ editor, node, getPos }: NodeViewProps) => {
  const navigate = useNavigate();

  const [syncedNodeId, setSyncedNodeId] = useState();
  const blockId = syncedNodeId ? syncedNodeId : node.attrs?.blockId;

  const [hover, setHover] = useState(false);
  const [hasChildList, setHasChildList] = useState(false);
  const [hasNodePage, setHasNodePage] = useState(false);
  const [collapsed, setCollapsed] = useState(getCollapsed(blockId));
  const [syncedCollapsed, setSyncedCollapsed] = useState(
    getSyncedCollapsed(node.attrs.syncedBlockId)
  );
  const { currentProject, pages } = useQueries();

  const extensions = loadExtensions(undefined, undefined, true);
  const [syncedText, setSyncedText] = useState("");
  const [syncedBullet, setSyncedBullet] = useState("");
  const editorRef = useRef<HTMLDivElement>(null);

  const { show } = useContextMenu({
    id: MENU_ID,
  });

  // console.log("node.props", node.attrs);
  // if (node.attrs) {
  //   console.log("HI", node.attrs);
  // }

  const pageVersions = pages
    // @ts-ignore bit hacky but works for now
    ?.filter((p) => p?.id == editor?.options?.editorProps.pageId)
    .sort((b, a) => {
      return new Date(a.created).getTime() - new Date(b.created).getTime();
    });
  const page = pageVersions?.[0];

  const { activeEditor, setActiveEditor } = useActiveEditor();

  const bulletOnClick = () => {
    navigate(`/project/${currentProject?.slug}/${blockId}`);
  };

  const collapseOnClick = async () => {
    storeCollapsed(blockId, !collapsed);
    setCollapsed(!collapsed);
    await setActiveEditor(undefined);
    setActiveEditor(activeEditor);
  };

  useEffect(() => {
    setHasChildList(Boolean(findChildOfType(node, "bulletList")));
    setHasNodePage(Boolean(pages.find((p) => p.id == blockId)));
  }, [node, pages]);

  const [upvotes, setUpvotes] = useState<number>(0);

  useEffect(() => {
    if (blockId in page.meta) {
      if (Array.isArray(page.meta[blockId])) {
        const votes = page.meta[blockId]
          .map((item) => item.value)
          .reduce((prev, next) => prev + next);

        setUpvotes(votes);
      } else {
        setUpvotes(page.meta[blockId]);
      }
    }
  }, [page]);

  function handleContextMenu(event) {
    show({
      event,
      props: {
        key: "value",
      },
    });
  }

  useEffect(() => {
    const getData = async () => {
      // console.log(node.attrs);
      // console.log("BLOOOO", node.attrs.syncedBlockId);
      setSyncedNodeId(node.attrs.syncedBlockId);
      const response = await django.get(`editor/node/${node?.attrs?.syncedBlockProject}/`, {
        params: {
          format: "json",
          block: node.attrs.syncedBlockId,
        },
      });
      const xmlFragment = convertXmlToYjs(response.data);
      const editorJson = yXmlFragmentToProsemirrorJSON(xmlFragment);
      const mainLabel = editorJson.content[0].content[0].content.map((item) => item.text).join(" ");
      const bulletList = editorJson.content[0].content[1];
      setSyncedBullet(bulletList);
      setSyncedText(mainLabel);

      // const isCollapsed = getSyncedCollapsed(node.attrs.syncedBlockId);

      if (bulletList) {
        const newDoc = {
          type: "doc",
          content: [bulletList],
        };
        console.log("newDoc", newDoc);

        new Editor({
          element: editorRef.current,
          extensions,
          editable: false,
          content: newDoc,
        });
      }
    };

    if (node?.attrs?.syncedBlockId && editorRef.current) {
      getData();
    }
  }, [node.attrs.syncedBlockId, editorRef]);

  if (node.attrs.syncedBlockId)
    return (
      <>
        <NodeViewWrapper
          className={classNames(styles.syncedNode, styles.listItem, {
            [styles.collapsed]: collapsed,
          })}
          as="li"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          data-blockid={blockId}
        >
          <div className={styles.menu}>
            {syncedBullet ? (
              <button
                onClick={async () => {
                  storeSyncedCollapsed(node.attrs.syncedBlockId, !syncedCollapsed);
                  setSyncedCollapsed(!syncedCollapsed);
                  await setActiveEditor(undefined);
                  setActiveEditor(activeEditor);
                }}
                title={"Show content"}
              >
                <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
              </button>
            ) : (
              <></>
            )}
            <button
              onClick={() => {
                const from = getPos() - 1;
                const to = from + node.nodeSize;

                editor.commands.deleteRange({ from, to });
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div
            className={classNames(styles.bullet, {
              [styles.hasNodePage]: hasNodePage,
            })}
            contentEditable={false}
            onClick={bulletOnClick}
          >
            <div className={styles.dot} contentEditable={false}></div>
          </div>
          <div className={styles.nodeContent} contentEditable={false}>
            <div
              className={classNames(styles.syncedNodeLabel, {
                [styles.soloSyncedNode]: Boolean(!syncedBullet || syncedCollapsed),
              })}
              contentEditable={false}
            >
              {syncedText ? syncedText : <FontAwesomeIcon icon={faSpinner} spin />}
            </div>
            <div
              ref={editorRef}
              contentEditable={false}
              style={{ display: !syncedCollapsed ? "block" : "none" }}
            ></div>
          </div>
          {/* {editor ? <EditorContent editor={readOnlyEditor} /> : <></>} */}
        </NodeViewWrapper>
      </>
    );

  return (
    <NodeViewWrapper
      onContextMenu={handleContextMenu}
      className={classNames(styles.listItem, { [styles.collapsed]: collapsed })}
      as="li"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-blockid={blockId}
    >
      {hasChildList ? (
        <div
          className={styles.collapse}
          contentEditable={false}
          onClick={collapseOnClick}
          style={{
            display: collapsed || hover ? "flex" : "none",
          }}
        >
          <FontAwesomeIcon icon={collapsed ? faAngleRight : faAngleDown} />
        </div>
      ) : (
        <></>
      )}
      <div
        className={classNames(styles.bullet, {
          [styles.hasNodePage]: hasNodePage,
        })}
        contentEditable={false}
        onClick={bulletOnClick}
      >
        <div className={styles.dot} contentEditable={false}></div>
      </div>
      <div className={styles.nodeContent}>
        {upvotes !== 0 ? (
          <div className={classNames(styles.upvotes, upvotes > 0 ? styles.green : styles.red)}>
            {upvotes > 0 ? `+${upvotes}` : `${upvotes}`}
          </div>
        ) : (
          <></>
        )}
        <NodeViewContent className={styles.content}></NodeViewContent>
      </div>
    </NodeViewWrapper>
  );
});

export const RoadmapListItem = ListItem.extend({
  priority: 1000,

  addAttributes() {
    return {
      ...this.parent?.(),
      syncedBlockId: {},
      syncedBlockProject: {},
    };
  },

  parseHTML() {
    return [{ tag: "li" }];
  },

  renderHTML({ HTMLAttributes }) {
    console.log(HTMLAttributes);
    return ["li", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});
